import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import SkeletonRectangle from "../../skeletons/SkeletonRectangle";
import Button from "../button/Button";
import {TableRow} from "./AsyncTableBody";
import AsyncTablePagination from "./AsyncTablePagination";
import {
    aggregateShortLabel,
    aggregationName,
    formatGroupValue,
    pathKey,
} from "./asyncTableGrouping";
import {describeGroupKey, formatDateUnitKey} from "./asyncTableGroupRules";
import "./_async-table-grouped.scss";

/**
 * Etykieta klucza grupy. Poziom z regułą ("Własne grupy") ma w kluczu indeks przedziału:
 * etykieta to wyrażenie i opcjonalna nazwa, a poza przedziałami "Pozostałe" i puste wartości.
 */
function getGroupKeyLabel(key, field, ctx, t) {
    const level = ctx.groupRules?.[field];
    const described =
        level && describeGroupKey(level.rule, level.filterType, key);
    if (described) {
        const label =
            described.kind === "bucket"
                ? described.label
                : t(
                      described.kind === "rest"
                          ? "grid.rest"
                          : "emptyGroupValue"
                  );
        return described.name ? `${label} · ${described.name}` : label;
    }
    // kolumna z datą grupowana po tygodniu / miesiącu - klucz to początek okresu
    const dateUnitLabel = formatDateUnitKey(
        key,
        ctx.groupDateUnits?.[field],
        t
    );
    if (dateUnitLabel) return dateUnitLabel;
    return (
        formatGroupValue(key, ctx.groupHeaders[field]) ?? t("emptyGroupValue")
    );
}

/**
 * Komórki wiersza grupy lub "Razem". Kolumny poziomów grupowania stoją na początku: poziomy wyżej puste,
 * etykieta w kolumnie poziomu grupy i dalej przez kolumny głębszych poziomów. Za nimi indeks (pusty)
 * i agregaty w swoich kolumnach - kilka agregatów jeden pod drugim, każdy z krótkim oznaczeniem typu.
 * @param {number} depth poziom grupy (0 - pierwszy, także "Razem")
 */
function GroupCells({label, aggs, depth, ctx}) {
    const {t} = useTranslation();
    const {headers, shouldIndex, aggregationsByField, levelsCount} = ctx;

    return (
        <>
            <AncestorCells depth={depth} />
            <td
                className="async-table-group-label"
                colSpan={levelsCount - depth}>
                {label}
            </td>
            {shouldIndex && <td />}
            {headers.slice(levelsCount).map((header, index) => {
                const list = aggregationsByField[header.field];
                if (!list) return <td key={index} />;
                return (
                    <td key={index} className="async-table-group-aggregate">
                        {list.map(({type, name}) => {
                            const value = formatGroupValue(
                                aggs?.[name],
                                header
                            );
                            return (
                                <div key={name}>
                                    {value !== null && (
                                        <span className="async-table-group-aggregate-type">
                                            {aggregateShortLabel(type, t)}
                                        </span>
                                    )}
                                    {value ?? "–"}
                                </div>
                            );
                        })}
                    </td>
                );
            })}
        </>
    );
}

/**
 * Kolumny poziomów nadrzędnych (wiersz na głębokości `depth` ma tylu przodków) - ciągła linia zagnieżdżenia.
 * Wiersze na całą szerokość (komunikat, wypełniacz, paginacja) zaczynają treść dopiero za nimi.
 */
function AncestorCells({depth, children}) {
    return Array.from({length: depth}, (_, index) => (
        <td key={`ancestor-${index}`} className="async-table-group-ancestor">
            {children}
        </td>
    ));
}

// szkielet ładowania - osobny prostokąt w każdej komórce, jak wiersz danych; wiersze danych mają wartości
// także w kolumnach poziomów (`isRows`), podgrupy nie - tam same linie zagnieżdżenia
function SkeletonRows({count, columnsCount, depth, isRows}) {
    return Array.from({length: count}, (_, index) => (
        <tr
            key={index}
            aria-hidden="true"
            className="async-table-group-skeleton loader"
            style={{"--group-depth": depth}}>
            <AncestorCells depth={depth}>
                {isRows && (
                    <>
                        &nbsp;
                        <SkeletonRectangle />
                    </>
                )}
            </AncestorCells>
            {Array.from({length: columnsCount - depth}, (_, cell) => (
                <td key={cell}>
                    &nbsp;
                    <SkeletonRectangle />
                </td>
            ))}
        </tr>
    ));
}

// puste wiersze dopełniające ostatnią stronę grupy do pełnej wysokości (tylko przy kilku stronach)
function FillerRows({count, columnsCount, depth}) {
    return Array.from({length: count}, (_, index) => (
        <tr
            key={index}
            aria-hidden="true"
            className="async-table-group-filler"
            style={{"--group-depth": depth}}>
            <AncestorCells depth={depth} />
            <td colSpan={columnsCount - depth}>&nbsp;</td>
        </tr>
    ));
}

function StatusRow({columnsCount, depth, className, children}) {
    return (
        <tr
            className={`async-table-group-status ${className ?? ""}`}
            style={{"--group-depth": depth}}>
            <AncestorCells depth={depth} />
            <td colSpan={columnsCount - depth}>{children}</td>
        </tr>
    );
}

/**
 * Stronicowanie wewnątrz rozwiniętej grupy - ten sam komponent co główna paginacja tabeli.
 */
function GroupPager({page, total, loading, onPage, ctx, depth, unit, owner}) {
    const {t} = useTranslation();
    const {rowsLimit, onRowsLimitChange, columnsCount} = ctx;

    // co liczy i czyje to strony: "Nr zwierzęcia 1–10 z 37 · Gr. 40", "wiersze 1–10 z 23 · 1024"
    const from = total === 0 ? 0 : (page - 1) * rowsLimit + 1;
    const to = Math.min(page * rowsLimit, total);
    const summary = t("groupPagerRange", {unit, from, to, total, owner});

    const setPage = useCallback(
        (value) => onPage(typeof value === "function" ? value(page) : value),
        [onPage, page]
    );

    return (
        <tr
            className="async-table-group-pager"
            style={{"--group-depth": depth}}>
            <AncestorCells depth={depth} />
            <td colSpan={columnsCount - depth}>
                {/* opis i przyciski przypięte do krawędzi widocznej części tabeli (_async-table-grouped.scss) */}
                <div className="async-table-group-pager-content">
                    <AsyncTablePagination
                        page={page}
                        setPage={setPage}
                        limit={rowsLimit}
                        setLimit={onRowsLimitChange}
                        maxItems={total}
                        disabled={loading}
                        summary={summary}
                    />
                </div>
            </td>
        </tr>
    );
}

/**
 * Zawartość rozwiniętej grupy: podgrupy (kolejny poziom) albo wiersze (ostatni poziom).
 * Przy kilku stronach każda strona ma pełną wysokość (wypełniacze), a szkielet ładowania tyle samo wierszy.
 */
function GroupChildren({node, path, group, owner, childUnit, depth, ctx}) {
    const {t} = useTranslation();
    const {groups, columnsCount, rowsLimit, headers, shouldIndex} = ctx;

    // podpis paginacji: wiersze danych albo podgrupy (nazwa kolumny następnego poziomu)
    const unit = node.isRows ? t("rows").toLowerCase() : childUnit;

    const onPage = useCallback(
        (page) => groups.setNodePage(path, page),
        [groups, path]
    );

    const onRetry = useCallback(() => groups.retry(path), [groups, path]);

    if (node.error) {
        return (
            <StatusRow
                columnsCount={columnsCount}
                depth={depth}
                className="error">
                <i className="fa-solid fa-exclamation-circle" />
                <span>{t("errorOccuredWhenFetchingData")}</span>
                <Button
                    type="button"
                    buttonStyle="text"
                    text={t("retry")}
                    onClick={onRetry}
                />
            </StatusRow>
        );
    }

    // liczba elementów grupy: wiersze znamy z wiersza grupy, podgrupy dopiero z odpowiedzi
    const total = node.isRows
        ? (node.response?.AllRecordsCount ?? group.count)
        : (node.response?.AllGroupsCount ?? null);
    const pages = total === null ? 1 : Math.ceil(total / rowsLimit);
    const multiPage = pages > 1;
    // wypełniacze tylko na poziomie wierszy danych - wysokość strony podgrup i tak zależy od rozwinięć
    const fill = multiPage && node.isRows;
    const page = node.page ?? 1;

    if (node.loading) {
        const onPageCount =
            total === null
                ? Math.min(rowsLimit, group.count)
                : Math.min(rowsLimit, total - (page - 1) * rowsLimit);
        return (
            <>
                <SkeletonRows
                    count={fill ? rowsLimit : onPageCount}
                    columnsCount={columnsCount}
                    depth={depth}
                    isRows={node.isRows}
                />
                {multiPage && (
                    <GroupPager
                        page={page}
                        total={total}
                        loading
                        onPage={onPage}
                        ctx={ctx}
                        depth={depth}
                        unit={unit}
                        owner={owner}
                    />
                )}
            </>
        );
    }

    if (!node.response) return null;

    let shown;
    let content;
    if (node.isRows) {
        const rows = node.response.Data ?? [];
        shown = rows.length;
        content = rows.map((row, index) => (
            <TableRow
                key={index}
                headers={headers}
                row={row}
                shouldIndex={shouldIndex}
                rowIndex={(page - 1) * rowsLimit + index}
                // indeks za kolumnami poziomów grupowania
                indexPosition={ctx.levelsCount}
                className="async-table-group-data"
                style={{"--group-depth": depth}}
            />
        ));
    } else {
        shown = node.response.Groups.length;
        content = (
            <GroupLevel
                response={node.response}
                parentPath={path}
                depth={depth}
                ctx={ctx}
            />
        );
    }

    if (shown === 0) {
        return (
            <StatusRow columnsCount={columnsCount} depth={depth}>
                {t("noRowsInGroupForFilters")}
            </StatusRow>
        );
    }

    return (
        <>
            {content}
            {fill && (
                <FillerRows
                    count={Math.max(0, rowsLimit - shown)}
                    columnsCount={columnsCount}
                    depth={depth}
                />
            )}
            {multiPage && (
                <GroupPager
                    page={page}
                    total={total}
                    onPage={onPage}
                    ctx={ctx}
                    depth={depth}
                    unit={unit}
                    owner={owner}
                />
            )}
        </>
    );
}

function GroupRow({group, response, parentPath, depth, ctx}) {
    const {t} = useTranslation();
    const {groups, groupHeaders, groupFields} = ctx;

    const path = useMemo(
        () => [...parentPath, {field: response.Field, value: group.key}],
        [parentPath, response.Field, group.key]
    );

    const node = groups.nodes[pathKey(path)];
    const expanded = !!node?.expanded;
    // spinner tylko przy pierwszym ładowaniu - przy zmianie strony szkielet jest w środku grupy
    const firstLoading = !!node?.loading && !node.response;

    const keyLabel = getGroupKeyLabel(group.key, response.Field, ctx, t);

    const onToggle = useCallback(
        () =>
            expanded
                ? groups.collapse(path)
                : groups.expand(path, response.IsLeafLevel),
        [expanded, groups, path, response.IsLeafLevel]
    );

    return (
        <>
            <tr
                className={`async-table-group-row level-${depth + 1}${expanded ? " expanded" : ""}`}
                style={{"--group-depth": depth}}>
                <GroupCells
                    aggs={group.aggs}
                    depth={depth}
                    ctx={ctx}
                    label={
                        <>
                            <button
                                type="button"
                                aria-expanded={expanded}
                                aria-label={t(expanded ? "collapse" : "expand")}
                                onClick={onToggle}>
                                <i
                                    className={
                                        firstLoading
                                            ? "fa-solid fa-spinner fa-spin fa-fw"
                                            : `fa-solid fa-chevron-${expanded ? "down" : "right"} fa-fw`
                                    }
                                />
                            </button>
                            <span className="async-table-group-key">
                                {keyLabel}
                            </span>
                            <span className="async-table-group-count">
                                {t("groupRowsCount", {count: group.count})}
                            </span>
                        </>
                    }
                />
            </tr>
            {expanded && (
                <GroupChildren
                    node={node}
                    path={path}
                    group={group}
                    owner={keyLabel}
                    childUnit={
                        groupHeaders[groupFields?.[path.length]]?.name ?? ""
                    }
                    depth={depth + 1}
                    ctx={ctx}
                />
            )}
        </>
    );
}

function GroupLevel({response, parentPath, depth, ctx}) {
    return response.Groups.map((group) => (
        <GroupRow
            key={JSON.stringify(group.key)}
            group={group}
            response={response}
            parentPath={parentPath}
            depth={depth}
            ctx={ctx}
        />
    ));
}

const EMPTY_PATH = [];

/**
 * Tabela w trybie grupowania: grupy poziomu 0 z rozwiniętymi poddrzewami i wiersz "Razem" na dole.
 * Stronicowanie poziomu 0 robi główna paginacja tabeli.
 * @param {*} param0
 * @returns
 */
export default function AsyncTableGroupedBody({
    groups,
    headers,
    shouldIndex,
    aggregations,
    groupHeaders,
    groupFields,
    groupRules,
    groupDateUnits,
    rowsLimit,
    onRowsLimitChange,
}) {
    const {t} = useTranslation();

    const columnsCount = headers.length + (shouldIndex ? 1 : 0);

    // kolumny poziomów grupowania stoją na początku tabeli
    const levelsCount = groupFields.length;

    // agregaty kolumny (pole -> [{type, name}]) w kolejności wysłanej do API; `name` to klucz w `aggs`
    // (pole_indeks, jak w podsumowaniu tabeli)
    const aggregationsByField = useMemo(() => {
        const result = {};
        for (const {type, name: field} of aggregations) {
            const list = result[field] ?? (result[field] = []);
            list.push({type, name: aggregationName(field, list.length)});
        }
        return result;
    }, [aggregations]);

    const ctx = useMemo(
        () => ({
            groups,
            headers,
            shouldIndex,
            aggregationsByField,
            groupHeaders,
            groupFields,
            groupRules,
            groupDateUnits,
            rowsLimit,
            onRowsLimitChange,
            columnsCount,
            levelsCount,
        }),
        [
            groups,
            headers,
            shouldIndex,
            aggregationsByField,
            groupHeaders,
            groupFields,
            groupRules,
            groupDateUnits,
            rowsLimit,
            onRowsLimitChange,
            columnsCount,
            levelsCount,
        ]
    );

    const {top, total} = groups;

    const onRetryTop = useCallback(() => groups.retry(EMPTY_PATH), [groups]);

    return (
        <>
            <tbody className="async-table-grouped">
                {top.error && (
                    <StatusRow
                        columnsCount={columnsCount}
                        depth={0}
                        className="error">
                        <i className="fa-solid fa-exclamation-circle" />
                        <span>{t("errorOccuredWhenFetchingData")}</span>
                        <Button
                            type="button"
                            buttonStyle="text"
                            text={t("retry")}
                            onClick={onRetryTop}
                        />
                    </StatusRow>
                )}
                {top.response?.Groups.length === 0 && (
                    <StatusRow columnsCount={columnsCount} depth={0}>
                        {t("noData")}
                    </StatusRow>
                )}
                {top.response && (
                    <GroupLevel
                        response={top.response}
                        parentPath={EMPTY_PATH}
                        depth={0}
                        ctx={ctx}
                    />
                )}
            </tbody>
            {total && (
                <tfoot className="async-table-grouped">
                    <tr className="async-table-group-total">
                        <GroupCells
                            aggs={total.aggs}
                            depth={0}
                            ctx={ctx}
                            label={
                                <>
                                    <span className="async-table-group-key">
                                        {t("total")}
                                    </span>
                                    <span className="async-table-group-count">
                                        {t("groupRowsCount", {
                                            count: total.count,
                                        })}
                                    </span>
                                </>
                            }
                        />
                    </tr>
                </tfoot>
            )}
        </>
    );
}
